import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  Box,
  Column,
  Columns,
  Tab,
  TabPanel,
  TabsBase,
  Text,
  useMediaQuery,
  useTheme,
} from "@origin-digital/ods-core";
import type { ResponsiveTabItem, ResponsiveTabsProps } from "./types";

const StyledVerticalTabsBase = styled(TabsBase)`
  border-bottom: none;

  .MuiTabs-flexContainer {
    border-bottom: none;
  }

  .MuiTabs-indicator {
    left: auto;
    right: 0;
    width: 4px;
    background-color: ${({ theme }) => theme.palette.secondary.main};
  }

  .MuiTab-root {
    align-items: flex-start;
    text-align: left;
    max-width: none;
    width: 100%;
    border-bottom: none;
  }

  .MuiTab-root.Mui-selected,
  .MuiTab-root:hover,
  .MuiTab-root:active {
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .MuiTab-root.Mui-selected,
  .MuiTab-root:active {
    background-color: ${({ theme }) =>
      theme.palette.backgroundColor.secondaryTone.light};
  }

  .MuiTab-root.Mui-focusVisible {
    outline: 2px solid ${({ theme }) => theme.palette.info.main};
    outline-offset: -2px;
  }
`;

const StyledHorizontalTabsBase = styled(TabsBase)`
  .MuiTabs-indicator {
    background-color: ${({ theme }) => theme.palette.secondary.main};
    height: 4px;
  }

  /* Keep both scroll chevrons present at all times. By default MUI hides the
     disabled one (e.g. left when at scroll start); QA requires both visible. */
  .MuiTabs-scrollButtons {
    opacity: 1;
    &.Mui-disabled {
      opacity: 0.3;
    }
  }
`;

const VerticalTabLabel = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  width: 100%;
`;

const ArticleColumnContent = styled.div`
  border-left: 1px solid ${({ theme }) => theme.palette.grey[200]};
  padding-left: 24px;
  height: 100%;
`;

const renderVerticalLabel = (item: ResponsiveTabItem, isSelected: boolean) => (
  <VerticalTabLabel gap="xxsmall">
    <Text variant="subtitle-small" weight="medium" tone="neutralDark">
      {item.label}
    </Text>
    {isSelected && item.subtitle && (
      <Text variant="body-small" tone="neutralDark">
        {item.subtitle}
      </Text>
    )}
  </VerticalTabLabel>
);

interface LayoutProps {
  items: ResponsiveTabItem[];
  activeValue: string;
  tabsProps: {
    value: string;
    onChange: (event: React.SyntheticEvent, newValue: string) => void;
    "aria-label": string;
    color: "primary";
  };
  dataId: string;
}

const renderDesktopLayout = ({
  items,
  activeValue,
  tabsProps,
  dataId,
}: LayoutProps) => (
  <div data-id={dataId}>
    <Columns collapseBelow="lg" space="none">
      <Column width="1/3">
        <StyledVerticalTabsBase
          {...tabsProps}
          muiProps={{ orientation: "vertical" }}
        >
          {items.map((item) => (
            <Tab
              key={item.id}
              value={item.id}
              label={renderVerticalLabel(item, activeValue === item.id)}
              data-id={`responsive-tab-${item.id}`}
            />
          ))}
        </StyledVerticalTabsBase>
      </Column>
      <Column width="2/3">
        <ArticleColumnContent data-id="responsive-tabs-divider">
          {items.map((item) => (
            <TabPanel
              key={item.id}
              value={item.id}
              show={activeValue === item.id}
              data-id={`responsive-tab-panel-${item.id}`}
            >
              {item.content}
            </TabPanel>
          ))}
        </ArticleColumnContent>
      </Column>
    </Columns>
  </div>
);

const renderMobileLayout = ({
  items,
  activeValue,
  tabsProps,
  dataId,
}: LayoutProps) => (
  <div data-id={dataId}>
    <StyledHorizontalTabsBase
      {...tabsProps}
      variant="scrollable"
      muiProps={{ scrollButtons: true, allowScrollButtonsMobile: true }}
    >
      {items.map((item) => (
        <Tab
          key={item.id}
          value={item.id}
          label={item.label}
          data-id={`responsive-tab-${item.id}`}
        />
      ))}
    </StyledHorizontalTabsBase>
    {items.map((item) => (
      <TabPanel
        key={item.id}
        value={item.id}
        show={activeValue === item.id}
        data-id={`responsive-tab-panel-${item.id}`}
      >
        {item.content}
      </TabPanel>
    ))}
  </div>
);

const isValidItemId = (
  items: ResponsiveTabItem[],
  id: string | undefined
): id is string => id !== undefined && items.some((item) => item.id === id);

const resolveValue = (
  items: ResponsiveTabItem[],
  preferred: string | undefined
): string => {
  if (isValidItemId(items, preferred)) return preferred;
  return items[0]?.id ?? "";
};

const useResponsiveTabValue = (
  items: ResponsiveTabItem[],
  defaultValue: string | undefined,
  value: string | undefined,
  onChange: ((value: string) => void) | undefined
) => {
  const [internalValue, setInternalValue] = useState<string>(() =>
    resolveValue(items, defaultValue)
  );
  const isControlled = value !== undefined;

  useEffect(() => {
    if (isControlled) return;
    if (isValidItemId(items, internalValue)) return;
    setInternalValue(resolveValue(items, defaultValue));
  }, [items, defaultValue, isControlled, internalValue]);

  const activeValue = isControlled ? value : internalValue;

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    if (!isControlled) setInternalValue(newValue);
    onChange?.(newValue);
  };

  return { activeValue, handleChange };
};

export const ResponsiveTabs: React.FC<ResponsiveTabsProps> = ({
  items,
  defaultValue,
  value,
  onChange,
  "aria-label": ariaLabel = "tabs",
  "data-id": dataId = "responsive-tabs",
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const { activeValue, handleChange } = useResponsiveTabValue(
    items,
    defaultValue,
    value,
    onChange
  );

  const tabsProps: LayoutProps["tabsProps"] = {
    value: activeValue,
    onChange: handleChange,
    "aria-label": ariaLabel,
    color: "primary",
  };
  const layoutProps: LayoutProps = { items, activeValue, tabsProps, dataId };

  return isDesktop
    ? renderDesktopLayout(layoutProps)
    : renderMobileLayout(layoutProps);
};
